import type { Signal } from '@preact/signals';
import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import {
  SceneRenderer,
  type AvalancheSceneData,
  type TriggerPreview,
} from '../render/SceneRenderer.ts';
import { overlayLegend } from '../render/overlayColors.ts';
import { pickNiceScale } from '../render/scaleBar.ts';
import type { HoverSample } from '../render/probe.ts';
import { snowFraction, temperatureAt } from '../sim/temperature.ts';
import {
  avalancheOverlayField,
  colorMode,
  crossSectionArmed,
  crossSectionLine,
  crossSectionPending,
  cumulativeDepositDepthM,
  disarmCrossSection,
  disarmTrigger,
  handleTerrainClick,
  hideParticles,
  hoverRelease,
  playback,
  previewRelease,
  runs,
  selectedRun,
  showDeposited,
  triggerArmed,
  triggerQueue,
  triggerSettings,
} from '../state/avalanche.ts';
import {
  activeOverlayField,
  AVALANCHE_OVERLAY_MODES,
  avalancheDepthDelta,
  conditions,
  displayConditions,
  displayEvent,
  displaySnowDepth,
  hoverProbe,
  overlay,
  selectedCell,
  showWindArrows,
  snowpack,
  terrain,
  uiMode,
  verticalExaggeration,
  type HoverProbe,
} from '../state/store.ts';
import { CrossSectionPanel } from './CrossSectionPanel.tsx';
import { formatNumber } from './format.ts';
import { HoverReadout } from './HoverReadout.tsx';
import { Legend } from './Legend.tsx';
import { PlaybackBar } from './PlaybackBar.tsx';
import { SnowProfilePanel } from './SnowProfilePanel.tsx';
import { TimelineBar } from './TimelineBar.tsx';

function toHoverProbe(sample: HoverSample): HoverProbe {
  // The displayed event's temperature (not `conditions.temperature`, which is only the default
  // for new events), so this matches the temperature/precip overlays and the timeline bar.
  const airTemp = temperatureAt(displayConditions.value.temperature, sample.elevation);
  const gridNorthBearing = terrain.value?.gridNorthBearing ?? 0;
  const probe: HoverProbe = {
    xUtm: sample.xUtm,
    yUtm: sample.yUtm,
    elevation: sample.elevation,
    slope: sample.slope,
    aspect: sample.aspect,
    snowDepth: sample.snowDepth,
    airTemp,
    snowFraction: snowFraction(airTemp),
  };
  if (sample.layerCount !== undefined) probe.layerCount = sample.layerCount;
  if (sample.topGrain !== undefined) probe.topGrain = sample.topGrain;
  if (sample.stormSnowfall !== undefined) probe.stormSnowfall = sample.stormSnowfall;
  if (sample.windChange !== undefined) probe.windChange = sample.windChange;
  if (sample.rain !== undefined) probe.rain = sample.rain;
  if (sample.windSpeed !== undefined) probe.windSpeed = sample.windSpeed;
  if (sample.windFromGridDeg !== undefined)
    probe.windFromTrueDeg = sample.windFromGridDeg + gridNorthBearing;
  if (sample.sx !== undefined) probe.sx = sample.sx;
  if (sample.skier !== undefined) probe.skier = sample.skier;
  if (sample.problem !== undefined) probe.problem = sample.problem;
  if (sample.criticalGrain !== undefined) probe.criticalGrain = sample.criticalGrain;
  if (sample.criticalDepth !== undefined) probe.criticalDepth = sample.criticalDepth;
  if (sample.avDebrisDepth !== undefined) probe.avDebrisDepth = sample.avDebrisDepth;
  if (sample.avRemovedDepth !== undefined) probe.avRemovedDepth = sample.avRemovedDepth;
  if (sample.avNetChange !== undefined) probe.avNetChange = sample.avNetChange;
  if (sample.avOriginSharePct !== undefined) probe.avOriginSharePct = sample.avOriginSharePct;
  if (sample.avMaxSpeed !== undefined) probe.avMaxSpeed = sample.avMaxSpeed;
  if (sample.avMaxPressureKPa !== undefined) probe.avMaxPressureKPa = sample.avMaxPressureKPa;
  return probe;
}

/** Reads only `overlay`/`displayConditions`/`terrain`/`displaySnowDepth`/the active field —
 * isolated in its own component so hover-probe updates (which fire on every animation frame while
 * the pointer moves) don't force a legend recompute. */
function SceneLegend() {
  const t = terrain.value;
  const depthOnTerrain = displaySnowDepth.value;
  if (!t || !depthOnTerrain) return null;
  const mode = overlay.value;
  const field = AVALANCHE_OVERLAY_MODES.has(mode)
    ? avalancheOverlayField.value
    : activeOverlayField.value;
  // Whether the timeline has a storm at all (up to the displayed hour, "computing…" vs. "no
  // storm" reads better than never explaining an empty storm overlay).
  const hasStorm = conditions.value.events.some((e) => e.kind === 'storm');
  const legend = overlayLegend(mode, t, depthOnTerrain, displayConditions.value, field, hasStorm);
  return <Legend legend={legend} />;
}

/** Reads only `hoverProbe` — isolated so it re-renders on every hover tick without dragging the
 * rest of the scene UI (in particular the legend's terrain-grid recompute) along with it. */
function SceneHoverReadout() {
  return <HoverReadout probe={hoverProbe.value} />;
}

/** Small badge next to the compass showing which way the wind blows relative to the current
 * camera view, and its reference speed — driven by the event at the hour on screen, so it hides
 * outside storms (or storms with no wind) and updates while scrubbing. Isolated so it can
 * re-render on every camera-change tick without dragging the rest of the scene UI along. */
function SceneWindIndicator({ azimuthDeg }: { azimuthDeg: Signal<number> }) {
  const t = terrain.value;
  const event = displayEvent.value;
  if (!t || !event || event.kind !== 'storm' || event.windSpeed <= 0) return null;
  const fromGrid = event.windFromDeg - t.gridNorthBearing;
  const rotation = azimuthDeg.value + fromGrid + 180;
  return (
    <div class="wind-indicator" title={`Wind from ${formatNumber(event.windFromDeg)}° true`}>
      <div class="wind-indicator-arrow" style={{ transform: `rotate(${rotation}deg)` }} />
      <span>{formatNumber(event.windSpeed, 0)}</span>
    </div>
  );
}

interface RendererRef {
  current: SceneRenderer | null;
}

/** Decodes and uploads one frame of the selected run's particle playback every time the
 * playback clock ticks — isolated so the 60 Hz time updates don't touch the rest of the scene
 * UI (in particular the big terrain `update()` pipeline). */
function SceneAvalanchePlayback({ rendererRef }: { rendererRef: RendererRef }) {
  const pb = playback.value;
  const mode = colorMode.value;
  const showDep = showDeposited.value;
  const hide = hideParticles.value;
  const run = pb ? runs.value.find((r) => r.id === pb.runId) : undefined;
  const frames = run ? run.result.frames : null;
  useEffect(() => {
    rendererRef.current?.updateParticles(
      frames,
      pb?.time ?? 0,
      mode,
      showDep,
      hide || !pb || !frames,
    );
  }, [frames, pb?.time, mode, showDep, hide]);
  return null;
}

/** Draws the cross-section tool's pending point / completed line on the terrain — changes only
 * on click, so it doesn't need the per-frame treatment playback does. */
function SceneCrossSectionSync({ rendererRef }: { rendererRef: RendererRef }) {
  const pending = crossSectionPending.value;
  const line = crossSectionLine.value;
  useEffect(() => {
    rendererRef.current?.setCrossSection(pending, line);
  }, [pending, line]);
  return null;
}

/** Draws a small numbered marker at each queued click, both modes — removed as each one starts
 * (`triggerQueue` drops its head when a run begins). */
function SceneQueuedMarkersSync({ rendererRef }: { rendererRef: RendererRef }) {
  const queue = triggerQueue.value;
  useEffect(() => {
    rendererRef.current?.setQueuedMarkers(queue.map((q, i) => ({ x: q.x, y: q.y, order: i + 1 })));
  }, [queue]);
  return null;
}

/** Preact wrapper around the vanilla three.js `SceneRenderer`: owns the DOM container, forwards
 * signal changes into `renderer.update()`, and mirrors hover samples into the `hoverProbe`
 * signal. The renderer itself has no knowledge of signals or the app's domain model beyond the
 * plain sim types it's handed. */
export function Scene3D() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<SceneRenderer | null>(null);
  const northRef = useRef<HTMLDivElement | null>(null);
  const scaleLabelRef = useRef<HTMLSpanElement | null>(null);
  const dockRef = useRef<HTMLDivElement | null>(null);

  // The legend and hover readout sit above the bottom dock, whose height changes as the
  // playback bar comes and goes: publish it as a CSS variable.
  useEffect(() => {
    const dock = dockRef.current;
    const scene = dock?.parentElement;
    if (!dock || !scene) return;
    const ro = new ResizeObserver(() => {
      scene.style.setProperty('--dock-height', `${dock.offsetHeight}px`);
    });
    ro.observe(dock);
    return () => ro.disconnect();
  }, []);
  const scaleBarRef = useRef<HTMLDivElement | null>(null);
  const azimuthDeg = useSignal(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const renderer = new SceneRenderer(container);
    rendererRef.current = renderer;
    renderer.setHoverHandler((sample) => {
      hoverProbe.value = sample ? toHoverProbe(sample) : null;
      // Simple mode always previews the natural release a click would set off, no arming needed;
      // advanced mode only previews while the trigger tool is armed.
      if (sample && (triggerArmed.peek() || uiMode.peek() === 'simple')) {
        const rel = previewRelease(sample.xUtm, sample.yUtm);
        hoverRelease.value = rel;
        const preview: TriggerPreview | null = rel
          ? {
              mode: uiMode.peek() === 'simple' ? 'natural' : triggerSettings.peek().mode,
              point: { x: sample.xUtm, y: sample.yUtm },
              radius: triggerSettings.peek().radius,
              cells: rel.cells,
            }
          : null;
        renderer.setTriggerPreview(preview);
      } else {
        hoverRelease.value = null;
        renderer.setTriggerPreview(null);
      }
    });
    renderer.setClickHandler((sample) => {
      handleTerrainClick(sample.xUtm, sample.yUtm, sample.snowpackCell);
    });

    const refreshCompass = (): void => {
      const deg = (renderer.getAzimuthalAngle() * 180) / Math.PI;
      azimuthDeg.value = deg;
      if (northRef.current) {
        // Azimuth +90° = camera east of target looking west, which puts grid north on the screen's
        // right: a clockwise rotation, i.e. positive CSS rotate(). True north sits at grid bearing
        // −gridNorthBearing, so it is rotated back by that much.
        const convergence = terrain.peek()?.gridNorthBearing ?? 0;
        northRef.current.style.transform = `rotate(${(deg - convergence).toFixed(1)}deg)`;
      }
      if (scaleBarRef.current && scaleLabelRef.current) {
        const metersPerPixel = renderer.getScaleMetersPerPixel();
        const meters = pickNiceScale(metersPerPixel);
        const px = metersPerPixel > 0 ? meters / metersPerPixel : 0;
        scaleBarRef.current.style.width = `${Math.max(1, px)}px`;
        scaleLabelRef.current.textContent = meters >= 1000 ? `${meters / 1000} km` : `${meters} m`;
      }
    };
    const unsubscribe = renderer.onCameraChange(refreshCompass);
    const resizeObserver = new ResizeObserver(refreshCompass);
    resizeObserver.observe(container);
    refreshCompass();

    return () => {
      unsubscribe();
      resizeObserver.disconnect();
      renderer.dispose();
      rendererRef.current = null;
      hoverProbe.value = null;
    };
  }, []);

  // Esc disarms whichever tool is currently armed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (triggerArmed.peek()) disarmTrigger();
      if (crossSectionArmed.peek()) disarmCrossSection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const mode = overlay.value;
  const isAvalancheOverlay = AVALANCHE_OVERLAY_MODES.has(mode);
  const effectiveOverlayField = isAvalancheOverlay
    ? avalancheOverlayField.value
    : activeOverlayField.value;
  const cumDeposit = cumulativeDepositDepthM.value;
  const net = avalancheDepthDelta.value;
  const run = selectedRun.value;
  const avalancheData: AvalancheSceneData | null =
    cumDeposit || net || run
      ? {
          cumulativeDeposit: cumDeposit,
          net,
          selectedRun: run
            ? {
                deposit: run.result.deposit,
                depositEntrained: run.result.depositEntrained,
                maxSpeed: run.result.maxSpeed,
                maxPressureKPa: run.result.maxPressureKPa,
              }
            : null,
          runout: run?.result.stats.runout ?? null,
          trajectory: run?.result.trajectory ?? null,
        }
      : null;

  useEffect(() => {
    const t = terrain.value;
    const renderer = rendererRef.current;
    // The scrubbed snapshot's depth while dragging the timeline, else the exact evaluated depth —
    // display only; avalanches always compute on the exact `snowDepthOnTerrain` (see avalanche.ts).
    const depthOnTerrain = displaySnowDepth.value;
    if (!t || !renderer || !depthOnTerrain) return;
    renderer.update({
      terrain: t,
      snowDepthOnTerrain: depthOnTerrain,
      snowpack: snowpack.value,
      overlay: mode,
      activeOverlayField: effectiveOverlayField,
      conditions: displayConditions.value,
      exaggeration: verticalExaggeration.value,
      showWindArrows: showWindArrows.value,
      selectedCell: selectedCell.value,
      avalanche: avalancheData,
    });
  }, [
    terrain.value,
    displaySnowDepth.value,
    snowpack.value,
    mode,
    effectiveOverlayField,
    displayConditions.value,
    verticalExaggeration.value,
    showWindArrows.value,
    selectedCell.value,
    cumDeposit,
    net,
    run,
  ]);

  return (
    <div class="scene3d">
      <div class="scene3d-canvas" ref={containerRef} />
      <SceneLegend />
      <SceneHoverReadout />
      <SnowProfilePanel />
      <CrossSectionPanel />
      <div class="bottom-dock" ref={dockRef}>
        <PlaybackBar />
        <TimelineBar />
      </div>
      <SceneAvalanchePlayback rendererRef={rendererRef} />
      <SceneCrossSectionSync rendererRef={rendererRef} />
      <SceneQueuedMarkersSync rendererRef={rendererRef} />
      {terrain.value && !snowpack.value ? (
        <div class="scene3d-note">Computing snowpack…</div>
      ) : null}
      <div class="compass" ref={northRef} aria-hidden="true">
        N
      </div>
      <SceneWindIndicator azimuthDeg={azimuthDeg} />
      <div class="scalebar">
        <div class="scalebar-bar" ref={scaleBarRef} />
        <span class="scalebar-label" ref={scaleLabelRef} />
      </div>
    </div>
  );
}
